import { useEffect, useRef, useState } from 'react';
import { ansagenFür, type Stand } from './ansagen';

/**
 * Gesprochene Ansagen.
 *
 * Gesprochen wird im **eigenen** Browser, nicht in der Browserquelle von OBS. Zwei Gründe:
 *
 * 1. Jeder soll für sich entscheiden, ob und wie laut er etwas hört. Eine Einstellung, die die
 *    Spielleitung für alle trifft, ist für die einen zu laut und für die anderen zu leise.
 * 2. Die Browserquelle in OBS bringt oft gar keine Stimmen mit – dort bliebe es ohnehin still.
 *
 * Benutzt wird die Sprachausgabe des Browsers. Die ist überall vorhanden, kostet nichts, braucht
 * keinen Dienst und keinen Schlüssel, und es verlässt kein Wort das Gerät.
 *
 * Was dabei zu beachten ist und hier erledigt wird:
 *
 * - **Browser sprechen erst nach einer Geste.** Vorher wird jeder Versuch stillschweigend
 *   verworfen. Deshalb wird das Einschalten als Geste genommen und sofort einmal gesprochen.
 * - **Stimmen laden verzögert.** Beim ersten Aufruf ist die Liste oft leer und füllt sich erst
 *   kurz darauf.
 * - **Was schon gesagt wurde, wird nicht wiederholt.** Dafür tragen Ansagen einen Schlüssel.
 */

export interface Spracheinstellung {
  an: boolean;
  lautstärke: number;
  tempo: number;
  /** Name der gewählten Stimme, oder leer für die Vorgabe des Browsers. */
  stimme: string;
}

const VORGABE: Spracheinstellung = { an: false, lautstärke: 0.8, tempo: 1.05, stimme: '' };
const SPEICHER = 'bingo.sprache';
const SPEICHER_UMFRAGEN = 'winchallenge.sprache.umfragen';

/** Höchstens so viele Schlüssel merken – mehr braucht niemand, und es soll nicht wachsen. */
const GEDÄCHTNIS = 200;

export function leseEinstellung(speicher = SPEICHER): Spracheinstellung {
  try {
    const roh = localStorage.getItem(speicher);
    if (!roh) return VORGABE;
    return { ...VORGABE, ...(JSON.parse(roh) as Partial<Spracheinstellung>) };
  } catch {
    // Privates Fenster, gesperrter Speicher, kaputter Eintrag: Die Vorgabe tut es auch.
    return VORGABE;
  }
}

function schreibeEinstellung(wert: Spracheinstellung, speicher = SPEICHER): void {
  try {
    localStorage.setItem(speicher, JSON.stringify(wert));
  } catch {
    // Nicht speichern zu können ist kein Grund, nicht zu sprechen.
  }
}

export function sprachausgabeVorhanden(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Hält die Einstellung und spricht, was neu ist.
 *
 * Der Stand wird hereingereicht; was daraus zu sagen ist, entscheidet `ansagenFür` – geprüft und
 * ohne Browser.
 */
export function useAnsagen(stand: Stand | null) {
  const [einstellung, setEinstellungIntern] = useState<Spracheinstellung>(() => leseEinstellung());
  const [umfrageEinstellung, setUmfrageEinstellungIntern] = useState<Spracheinstellung>(() => leseEinstellung(SPEICHER_UMFRAGEN));
  const [stimmen, setStimmen] = useState<SpeechSynthesisVoice[]>([]);
  const vorheriger = useRef<Stand | null>(null);
  const gesagt = useRef<string[]>([]);

  // Die Stimmenliste ist beim ersten Aufruf oft leer und füllt sich erst kurz darauf.
  useEffect(() => {
    if (!sprachausgabeVorhanden()) return;

    const laden = () => setStimmen(window.speechSynthesis.getVoices());
    laden();
    window.speechSynthesis.addEventListener('voiceschanged', laden);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', laden);
  }, []);

  function sprich(text: string, mit = einstellung, fertig?: () => void): void {
    if (!mit.an || !sprachausgabeVorhanden()) return;

    try {
      const äußerung = new SpeechSynthesisUtterance(text);
      äußerung.lang = 'de-DE';
      if (fertig) äußerung.onend = fertig;
      äußerung.volume = Math.max(0, Math.min(1, mit.lautstärke));
      äußerung.rate = Math.max(0.5, Math.min(2, mit.tempo));

      const verfügbar = window.speechSynthesis.getVoices();
      const gewählt = verfügbar.find((s) => mit.stimme && s.name === mit.stimme && /^de/i.test(s.lang))
        ?? verfügbar.find((s) => /^de/i.test(s.lang) && s.default)
        ?? verfügbar.find((s) => /^de/i.test(s.lang));
      if (gewählt) äußerung.voice = gewählt;

      window.speechSynthesis.speak(äußerung);
    } catch {
      // Eine stumme Ansage ist ärgerlich, ein Absturz wäre schlimmer.
    }
  }

  useEffect(() => {
    if (!stand) return;

    const vorher = vorheriger.current;
    vorheriger.current = stand;
    if (!einstellung.an && !umfrageEinstellung.an) return;

    for (const ansage of ansagenFür(vorher, stand)) {
      if (gesagt.current.includes(ansage.schlüssel)) continue;
      gesagt.current.push(ansage.schlüssel);
      if (gesagt.current.length > GEDÄCHTNIS) gesagt.current.shift();
      const istUmfrage = ansage.schlüssel.startsWith('poll-') || ansage.schlüssel.startsWith('challenge-');
      sprich(ansage.text, istUmfrage ? umfrageEinstellung : einstellung);
    }
    // `sprich` hängt an der Einstellung, die hier bereits gelesen wird.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand, einstellung, umfrageEinstellung]);

  const letzteVorbereitung = useRef<string | null>(null);
  const letzteSekunde = useRef<number | null>(null);
  useEffect(() => {
    const bis = stand?.timer.preparationEndsAt;
    if (!bis || stand?.timer.phase !== 'IDLE' || !einstellung.an) {
      letzteVorbereitung.current = null;
      letzteSekunde.current = null;
      return;
    }
    const tick = () => {
      const rest = Math.max(0, Math.ceil((Date.parse(bis) - Date.now()) / 1000));
      if (letzteVorbereitung.current !== bis) {
        letzteVorbereitung.current = bis;
        letzteSekunde.current = rest;
        return;
      }
      if (rest === letzteSekunde.current) return;
      letzteSekunde.current = rest;
      if (rest <= 10 && rest >= 2) sprich(String(rest));
      if (rest === 1) sprich('1', einstellung, () => sprich('LOS!'));
    };
    tick();
    const uhr = setInterval(tick, 150);
    return () => clearInterval(uhr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand?.timer.preparationEndsAt, stand?.timer.phase, einstellung.an]);

  // Die Spielzeit wird anhand des letzten Serverstands lokal fortgeschrieben.
  // Pro Runde/Schwelle wird höchstens einmal angesagt, auch bei häufigen Live-Updates.
  const spielZeit = useRef({ phase: '', remaining: 0, receivedAt: 0, duration: 0 });
  const spielGesagt = useRef<Set<string>>(new Set());
  // Eindeutige Generation: alte asynchrone Sprach-Callbacks dürfen nicht in neue Runden hineinsprechen.
  const spielGeneration = useRef(0);
  useEffect(() => {
    if (!stand) return;
    const timer = stand.timer;
    const vorher = spielZeit.current;
    // Eine neue Runde kann direkt nach ENDED/EXPIRED starten, ohne dass ein
    // IDLE-Zustand beim Browser ankommt. Dann müssen die Ansageschwellen
    // erneut verfügbar sein; PAUSED -> RUNNING ist dagegen nur Fortsetzen.
    const neueRunde = timer.phase === 'RUNNING' &&
      (vorher.phase === 'ENDED' || vorher.phase === 'EXPIRED' || vorher.phase === 'IDLE');
    if ((timer.phase === 'IDLE' && vorher.phase !== 'IDLE') || neueRunde) {
      spielGesagt.current.clear();
      spielGeneration.current += 1;
    }
    spielZeit.current = { phase: timer.phase, remaining: timer.remainingSeconds,
      receivedAt: Date.now(), duration: timer.durationSeconds };
  }, [stand?.timer]);

  useEffect(() => {
    if (!einstellung.an) return;
    const tick = () => {
      const t = spielZeit.current;
      if (t.phase !== 'RUNNING') return;
      const rest = Math.max(0, Math.ceil(t.remaining - (Date.now() - t.receivedAt) / 1000));
      const thresholds: [string, number, string][] = [
        ['half', Math.floor(t.duration / 2), 'Die Hälfte der Zeit ist rum.'],
        ['third', Math.floor(t.duration / 3), `Ihr habt noch ${Math.ceil(t.duration / 180)} Minuten.`],
        ['five', 300, 'Ihr habt noch 5 Minuten.'],
        ['minute', 60, 'Ihr habt noch 60 Sekunden.'],
      ];
      for (const [id, threshold, text] of thresholds) {
        if (threshold <= 0 || threshold >= t.duration || rest > threshold || spielGesagt.current.has(id)) continue;
        // Bei sehr kurzen Runden nicht mehrere vergangene Schwellen auf einmal nachholen.
        if (rest < threshold - 2) { spielGesagt.current.add(id); continue; }
        spielGesagt.current.add(id);
        sprich(text);
      }
      if (rest >= 1 && rest <= 10) {
        const id = `end-${rest}`;
        if (!spielGesagt.current.has(id)) {
          spielGesagt.current.add(id);
          if (rest === 1) {
            const generation = spielGeneration.current;
            sprich('1', einstellung, () => {
              const aktuell = spielZeit.current;
              const restJetzt = aktuell.remaining - (Date.now() - aktuell.receivedAt) / 1000;
              if (spielGeneration.current === generation && aktuell.phase === 'RUNNING' && restJetzt <= 1) {
                sprich('STOP!');
              }
            });
          }
          else sprich(String(rest));
        }
      }
    };
    const timer = setInterval(tick, 150);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [einstellung.an, einstellung.lautstärke, einstellung.tempo]);

  function aktualisieren(
    bisher: Spracheinstellung,
    änderung: Partial<Spracheinstellung>,
    speichern: (neu: Spracheinstellung) => void,
    schlüssel: string,
  ): void {
    const neu = { ...bisher, ...änderung };
    speichern(neu);
    schreibeEinstellung(neu, schlüssel);

    // Das Einschalten ist die Geste, auf die Browser warten – also gleich einmal sprechen. Das
    // ist zugleich die Probe: Wer nichts hört, weiß sofort, dass etwas nicht stimmt.
    if (änderung.an === true) {
      // Bereits laufende Ansagen des anderen Bereichs nicht abbrechen.
      sprich('Ansagen sind an.', neu);
    }
    if (änderung.an === false) {
      // Die Browser-Speech-Queue wird gemeinsam von Bingo und Challenges benutzt.
      // Deshalb nur abbrechen, wenn tatsächlich beide Bereiche ausgeschaltet sind.
      const andererBereichAn = schlüssel === SPEICHER
        ? umfrageEinstellung.an : einstellung.an;
      if (!andererBereichAn && sprachausgabeVorhanden()) {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* nicht weiter schlimm */
        }
      }
    }
  }

  function setzeEinstellung(änderung: Partial<Spracheinstellung>): void {
    aktualisieren(einstellung, änderung, setEinstellungIntern, SPEICHER);
  }

  function setzeUmfrageEinstellung(änderung: Partial<Spracheinstellung>): void {
    aktualisieren(umfrageEinstellung, änderung, setUmfrageEinstellungIntern, SPEICHER_UMFRAGEN);
  }

  return {
    einstellung,
    setzeEinstellung,
    umfrageEinstellung,
    setzeUmfrageEinstellung,
    /** Nur deutsche Stimmen – die übrigen sprechen die Ansagen unverständlich aus. */
    stimmen: stimmen.filter((s) => s.lang.toLowerCase().startsWith('de')),
    vorhanden: sprachausgabeVorhanden(),
    probe: () => sprich('Bingo für Rot!', einstellung),
    umfrageProbe: () => sprich('Die nächste Challenge startet jetzt!', umfrageEinstellung),
  };
}
